import { Icon } from 'antd';
import HowToFix from '../../shared/HowToFix';
import SectionCompetitorResults from '../../shared/SectionCompetitorResults';
import SectionMessage from '../../shared/SectionMessage';
import SectionTitle from '../../shared/SectionTitle';
import SectionTooltip from '../../shared/SectionTooltip';
import { SectionProps } from '../../types';
import './HttpsEncryption.less';
import React from 'react';
import { getIconForStatus } from '../../utils';
import moment from 'moment';

type Certificate = {
    common_name: string;
    issuer: string;
    location: string;
    organization: string;
    sans: string[];
    signature_algorithm: string;
    valid_from: number;
    valid_to: number;
};

function getCertificateLabel(chainLength: number, index: number) {
    let label = '';

    if (index === chainLength - 1) {
        label = 'Root certificate';
    } else if (index < chainLength - 1 && index !== 0) {
        label = 'Intermediate certificate';
    } else {
        label = 'Server certificate';
    }

    return label;
}

function formatDateFromTimestamp(timestamp: number, formatStr = 'ddd, MMMM Do YYYY, h:mm:ss a (z)') {
    try {
        return moment.unix(timestamp).utc().format(formatStr);
    } catch (e) {}
    return '';
}

export default function HttpsEncryptionSection({ sectionKey, title, url, relevance, tooltip, result, competitorsResults, passRate }: SectionProps) {
    const hasCompetitors = competitorsResults && Object.keys(competitorsResults).length > 0;

    return (
        <div
            id={sectionKey}
            className={`report-element ${result.status}`}
        >
            <SectionTitle
                title={title}
                status={result.status}
                passRate={passRate}
            />
            <div className="report-element-description">
                <SectionMessage
                    url={url}
                    status={result.status}
                    relevance={relevance}
                    message={result.message}
                    hasCompetitors={hasCompetitors}
                />

                <HttpEncryptionResult
                    subResults={result.sub_results}
                    certificateChain={result.certificate_chain}
                    checkTimestamp={result.check_timestamp}
                />

                {result.how_to_fix && (
                    <HowToFix
                        title={title}
                        howToFix={result.how_to_fix}
                        howToFixVideo={result.how_to_fix_video}
                    />
                )}

                {hasCompetitors && (
                    <SectionCompetitorResults
                        results={competitorsResults}
                        relevance={relevance}
                    />
                )}
            </div>

            <SectionTooltip tooltip={tooltip} />
        </div>
    );
}

type HttpEncryptionResultProps = {
    subResults?: {
        key: string;
        message: string;
        status: string;
    }[];
    certificateChain?: Certificate[];
    checkTimestamp: number;
};

function HttpEncryptionResult({ subResults, checkTimestamp, certificateChain }: HttpEncryptionResultProps) {
    return (
        <div className="description-meta">
            {subResults && (
                <div className="sub-results">
                    {subResults.map((subResult, idx) => {
                        return (
                            <div
                                className={`sub-result ${subResult.status}`}
                                key={idx}
                            >
                                <Icon
                                    type={getIconForStatus(subResult.status)}
                                    theme="filled"
                                    className="status-icon"
                                />
                                <p>{subResult.message}</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {certificateChain && certificateChain.length > 0 && (
                <CertificateChain
                    chain={certificateChain}
                    checkTimestamp={checkTimestamp}
                />
            )}
        </div>
    );
}

type CertificateChainProps = {
    chain: Certificate[];
    checkTimestamp: number;
};

function CertificateChain({ chain, checkTimestamp }: CertificateChainProps) {
    return (
        <div className="certificate-chain">
            <span className="title">Certificate Chain:</span>

            {chain.map((cert, idx) => {
                const label = getCertificateLabel(chain.length, idx);

                return (
                    <div
                        className="stats-table-container"
                        key={idx}
                    >
                        <header>
                            <div className="col">{label}</div>
                            <div className="col"></div>
                        </header>

                        <div className="row">
                            <div className="col capitalize">Common name</div>
                            <div className="col">{cert.common_name}</div>
                        </div>

                        {cert.organization && (
                            <div className="row">
                                <div className="col capitalize">Organization</div>
                                <div className="col">{cert.organization}</div>
                            </div>
                        )}

                        {cert.location && (
                            <div className="row">
                                <div className="col capitalize">Location</div>
                                <div className="col">{cert.location}</div>
                            </div>
                        )}

                        {cert.sans && cert.sans.length > 0 && (
                            <div className="row">
                                <div className="col capitalize">Subject Alternative Names (SANs)</div>
                                <div className="col">{cert.sans.join(', ')}</div>
                            </div>
                        )}

                        <div className={`row ${cert.valid_from > checkTimestamp ? 'failed' : ''}`}>
                            <div className="col capitalize">Not valid before</div>
                            <div className="col">{formatDateFromTimestamp(cert.valid_from)}</div>
                        </div>

                        <div className={`row ${cert.valid_to < checkTimestamp ? 'failed' : ''}`}>
                            <div className="col capitalize">Not valid after</div>
                            <div className="col">{formatDateFromTimestamp(cert.valid_to)}</div>
                        </div>

                        {cert.signature_algorithm && (
                            <div className="row">
                                <div className="col capitalize">Signature algorithm</div>
                                <div className="col">{cert.signature_algorithm}</div>
                            </div>
                        )}

                        <div className="row">
                            <div className="col capitalize">Issuer</div>
                            <div className="col">{cert.issuer}</div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
